import { css } from '@emotion/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalEditor } from 'lexical';
import { useEffect, useState } from 'react';

export interface TextRange {
  start: number;
  /** Exclusive. A collapsed range (start === end) underlines the char before it. */
  end: number;
}

interface UnderlineRect {
  left: number;
  top: number;
  width: number;
}

const WAVY_SVG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='6' height='3'%3E%3Cpath d='M0 2.5 Q1.5 0.5 3 2.5 T6 2.5' stroke='%23D04246' fill='none' stroke-width='1'/%3E%3C/svg%3E\")";

const underlineCss = css`
  position: absolute;
  height: 3px;
  pointer-events: none;
  background-image: ${WAVY_SVG};
  background-repeat: repeat-x;
  background-position: bottom;
`;

/**
 * Offsets are into the display text: the concatenation of the editor's DOM
 * text nodes with "\n" per <br>, which is exactly what $serializeFormula's
 * display rendering produces. Walking the DOM (not the Lexical nodes) keeps
 * this an overlay concern — no editor state is touched, undo history stays
 * clean.
 */
function computeUnderlineRects(editor: LexicalEditor, range: TextRange): UnderlineRect[] {
  const rootElement = editor.getRootElement();
  const container = rootElement?.parentElement;
  if (!rootElement || !container) {
    return [];
  }
  const start = range.start === range.end ? Math.max(0, range.start - 1) : range.start;
  const end = Math.max(range.end, start + 1);

  const rects: UnderlineRect[] = [];
  const containerRect = container.getBoundingClientRect();
  const walker = document.createTreeWalker(rootElement, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let offset = 0;
  for (let node = walker.nextNode(); node && offset < end; node = walker.nextNode()) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      if ((node as Element).tagName === 'BR') {
        offset += 1;
      }
      continue;
    }
    const text = node as Text;
    const nodeEnd = offset + text.data.length;
    if (nodeEnd > start && offset < end) {
      try {
        const domRange = document.createRange();
        domRange.setStart(text, Math.max(start, offset) - offset);
        domRange.setEnd(text, Math.min(end, nodeEnd) - offset);
        for (const rect of domRange.getClientRects()) {
          if (rect.width > 0) {
            rects.push({
              left: rect.left - containerRect.left,
              top: rect.bottom - containerRect.top - 3,
              width: rect.width,
            });
          }
        }
      } catch {
        // A stale range (text changed under a debounced error) must never throw.
      }
    }
    offset = nodeEnd;
  }
  return rects;
}

/** Renders a wavy error underline over a display-text range, as siblings inside the positioned container. */
export function ErrorUnderlinePlugin({ range }: { range: TextRange | null }) {
  const [editor] = useLexicalComposerContext();
  const [rects, setRects] = useState<UnderlineRect[]>([]);

  useEffect(() => {
    const compute = () => setRects(range ? computeUnderlineRects(editor, range) : []);
    compute();
    const unregister = editor.registerUpdateListener(compute);
    window.addEventListener('resize', compute);
    return () => {
      unregister();
      window.removeEventListener('resize', compute);
    };
  }, [editor, range]);

  return (
    <>
      {rects.map((rect) => (
        <span
          key={`${rect.left}:${rect.top}:${rect.width}`}
          data-testid='formula-error-underline'
          css={underlineCss}
          style={{ left: rect.left, top: rect.top, width: rect.width }}
        />
      ))}
    </>
  );
}
